// TicketTypeDonutChart.jsx - FIXED: Function order corrected
import React, { useState, useMemo } from "react";

// ✅ Helper function defined OUTSIDE component (no temporal dead zone)
const getColorForType = (type) => {
  const colors = {
    'General': '#f97316',
    'VIP': '#3b82f6',
    'Speaker': '#10b981',
    'Sponsor': '#8b5cf6',
    'Press': '#ec4899',
    'Staff': '#64748b'
  };
  return colors[type] || '#94a3b8';
};

const TicketTypeDonutChart = ({ data = [] }) => {
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, data: null });
  
  // ✅ Process real API data - getColorForType is now defined above
  const chartData = useMemo(() => {
    if (!data || data.length === 0) {
      return { items: [], total: 0 };
    }
    
    // Transform API data format { type, count } to chart format
    const items = data.map(item => ({
      label: item.type || 'Unknown',
      value: item.count || 0,
      count: item.count || 0,
      color: getColorForType(item.type)  // ✅ Now works!
    }));
    
    const total = items.reduce((sum, item) => sum + item.value, 0);
    
    return { items, total };
  }, [data]);

  const { items, total } = chartData;
  const radius = 80;
  let currentStartAngle = 0;
  
  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const centerX = 300;
    const centerY = 150;
    const dx = x - centerX;
    const dy = y - centerY;
    const angle = Math.atan2(dy, dx) * (180 / Math.PI);
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    if (distance > 40 && distance < radius) {
      let segment = null;
      let tempAngle = 0;
      
      for (const item of items) {
        const segmentAngle = (item.value / total) * 360;
        if (angle >= tempAngle && angle < tempAngle + segmentAngle) {
          segment = item;
          break;
        }
        tempAngle += segmentAngle;
      }
      
      if (segment) {
        setTooltip({
          visible: true,
          x: x + 20,
          y: y - 20,
          data: segment
        });
      } else {
        setTooltip({ visible: false });
      }
    } else {
      setTooltip({ visible: false });
    }
  };
  
  const handleMouseLeave = () => {
    setTooltip({ visible: false });
  };

  // Show empty state if no data
  if (!data || data.length === 0 || total === 0) {
    return (
      <div className="ticket-type-container">
        <h2 className="ticket-type-title">Ticket Type Breakdown</h2>
        <div className="chart-empty-state">
          <p>No ticket type data available</p>
        </div>
      </div>
    );
  }

  // Reset angle for rendering
  currentStartAngle = 0;

  return (
    <div className="ticket-type-container">
      <h2 className="ticket-type-title">Ticket Type Breakdown</h2>
      
      <div 
        className="donut-chart"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <svg viewBox="0 0 600 300" xmlns="http://www.w3.org/2000/svg">
          <g transform="translate(300, 150)">
            {items.map((item, index) => {
              const segmentAngle = (item.value / total) * 360;
              const start = currentStartAngle;
              const end = currentStartAngle + segmentAngle;
              
              const x1 = radius * Math.cos(start * Math.PI / 180);
              const y1 = radius * Math.sin(start * Math.PI / 180);
              const x2 = radius * Math.cos(end * Math.PI / 180);
              const y2 = radius * Math.sin(end * Math.PI / 180);
              
              const largeArc = segmentAngle > 180 ? 1 : 0;
              const path = `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} L 0 0 Z`;
              
              currentStartAngle += segmentAngle;
              
              return (
                <path
                  key={index}
                  d={path}
                  fill={item.color}
                  strokeWidth="1"
                  stroke="#fff"
                />
              );
            })}
            
            <circle cx="0" cy="0" r="40" fill="white" />
            <circle cx="0" cy="0" r={radius} fill="transparent" />
          </g>
        </svg>
        
        {/* Tooltip */}
        {tooltip.visible && tooltip.data && (
          <div 
            className="chart-tooltip" 
            style={{ 
              left: tooltip.x, 
              top: tooltip.y,
              transform: 'translateX(-50%)'
            }}
          >
            <div className="tooltip-content">
              <span className="tooltip-label">{tooltip.data.label}:</span>
              <span className="tooltip-value">{tooltip.data.count}</span>
            </div>
          </div>
        )}
      </div>
      
      {/* Legend */}
      <div className="chart-legend" style={{ justifyContent: 'center', gap: '16px' }}>
        {items.map((item, index) => (
          <div key={index} className="chart-legend-item">
            <div className="chart-legend-color" style={{ backgroundColor: item.color }}></div>
            <span className="chart-legend-label">{item.label}</span>
          </div>
        ))}
      </div>
      
      {/* Statistics */}
      <div className="chart-stats">
        <div className="stat-item">
          <span className="stat-label">Total Tickets:</span>
          <span className="stat-value">{total}</span>
        </div>
        {items.length > 0 && (
          <div className="stat-item">
            <span className="stat-label">Most Popular:</span>
            <span className="stat-value">
              {items.reduce((max, item) => item.value > max.value ? item : max).label}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketTypeDonutChart;